import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Clock, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestCheckin() {
  const { guestId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [guest, setGuest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!guestId) { setStatus("error"); setError("No check-in code"); return; }

    // First check guest status
    axios.get(`${API}/guests/${guestId}/status`)
      .then(res => {
        const g = res.data;
        setGuest(g);
        if (g.status === "checked_in") {
          setStatus("already");
        } else {
          // Auto check-in
          axios.put(`${API}/guests/${guestId}/check-in`)
            .then(r => {
              setGuest({ ...g, ...r.data });
              setStatus("success");
            })
            .catch(() => { setStatus("error"); setError("Check-in failed"); });
        }
      })
      .catch(() => { setStatus("error"); setError("Invalid check-in code"); });
  }, [guestId]);

  if (status === "loading") return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-4 animate-pulse" />
        <p className="text-lg font-heading font-semibold">Checking you in...</p>
      </div>
    </div>
  );

  if (status === "error") return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 text-center border" data-testid="checkin-error">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h1 className="font-heading font-bold text-xl mb-2">Check-in Error</h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/guest-register")} data-testid="go-register-btn">
          Register as Guest Instead
        </Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 text-center border" data-testid="checkin-success">
        <div className={`inline-flex items-center justify-center h-20 w-20 rounded-full mb-6 mx-auto ${status === "already" ? "bg-blue-50" : "bg-emerald-50"}`}>
          <Check className={`h-10 w-10 ${status === "already" ? "text-blue-600" : "text-emerald-600"}`} strokeWidth={2.5} />
        </div>
        <h1 className="font-heading font-bold text-2xl mb-2" data-testid="checkin-greeting">
          {status === "already" ? "Already Checked In" : `Welcome, ${guest?.guest_name || guest?.name}!`}
        </h1>
        <p className="text-muted-foreground mb-2">
          {status === "already"
            ? `${guest?.name}, you're already checked in.`
            : "You've been checked in successfully."}
        </p>
        {guest?.host_name && (
          <p className="text-sm text-muted-foreground mb-6">
            <strong>{guest.host_name}</strong> has been notified of your arrival.
          </p>
        )}
        <div className="text-xs text-muted-foreground mt-4">
          Welcome to The Rig
        </div>
      </Card>
    </div>
  );
}
